import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, Trophy, Medal, Award, Crosshair, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { AccountBadge } from "@/components/account-badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const ANY = "__any__";

type Option = { value: string; label: string };

type LeaderRow = {
  rank: number;
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  account_type: any;
  verified: boolean | null;
  is_founder: boolean | null;
  level: number | null;
  civic_score: number | null;
  impact_score: number | null;
  country: string | null;
  city: string | null;
  distance_km: number | null;
};

export function CivicLeaderboard() {
  const { user, profile } = useAuth();
  const [country, setCountry] = useState<string>(ANY);
  const [city, setCity] = useState<string>(ANY);
  const [community, setCommunity] = useState<string>(ANY);
  const [radiusOn, setRadiusOn] = useState(false);
  const [radius, setRadius] = useState(25);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [options, setOptions] = useState<{ country: Option[]; city: Option[]; community: Option[] }>({
    country: [], city: [], community: [],
  });
  const [rows, setRows] = useState<LeaderRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.rpc("leaderboard_filter_options" as never);
      const grouped = { country: [] as Option[], city: [] as Option[], community: [] as Option[] };
      for (const r of (data as unknown as any[]) ?? []) {
        if (!r?.value) continue;
        (grouped as any)[r.kind]?.push({ value: r.value, label: r.label ?? r.value });
      }
      setOptions(grouped);
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("civic_leaderboard" as never, {
      _country: country === ANY ? null : country,
      _city: city === ANY ? null : city,
      _community: community === ANY ? null : community,
      _lat: radiusOn && coords ? coords.lat : null,
      _lng: radiusOn && coords ? coords.lng : null,
      _radius_km: radiusOn && coords ? radius : null,
      _limit: 100,
    } as never);
    if (error) toast.error("Could not load rankings");
    setRows(((data as unknown as any[]) ?? []) as LeaderRow[]);
    setLoading(false);
  }, [country, city, community, radiusOn, coords, radius]);

  useEffect(() => { void load(); }, [load]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location is not available on this device"); return; }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setRadiusOn(true);
      },
      () => toast.error("Location permission denied"),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  };

  const reset = () => {
    setCountry(ANY); setCity(ANY); setCommunity(ANY); setRadiusOn(false);
  };

  const myRow = useMemo(() => rows.find(r => r.id === user?.id) ?? null, [rows, user?.id]);
  const filtersActive = country !== ANY || city !== ANY || community !== ANY || radiusOn;

  return (
    <div className="space-y-4">
      <Card className="space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <FilterSelect
            label="Country"
            value={country}
            onChange={setCountry}
            placeholder="All countries"
            options={options.country}
            myValue={profile?.country ?? null}
          />
          <FilterSelect
            label="City"
            value={city}
            onChange={setCity}
            placeholder="All cities"
            options={options.city}
            myValue={profile?.city ?? null}
          />
          <FilterSelect
            label="Space"
            value={community}
            onChange={setCommunity}
            placeholder="All Spaces"
            options={options.community}
          />
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">City radius</span>
              {radiusOn && coords ? (
                <span className="text-xs text-muted-foreground">within {radius} km of you</span>
              ) : (
                <span className="text-xs text-muted-foreground">off</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {radiusOn && (
                <Button size="sm" variant="ghost" onClick={() => setRadiusOn(false)}>Turn off</Button>
              )}
              <Button size="sm" variant="outline" onClick={useMyLocation}>
                <Crosshair className="mr-1.5 h-3.5 w-3.5" />
                {coords ? "Update location" : "Use my location"}
              </Button>
            </div>
          </div>
          {radiusOn && coords && (
            <div className="mt-3 flex items-center gap-3">
              <Slider
                value={[radius]}
                onValueChange={v => setRadius(v[0] ?? 25)}
                min={1}
                max={200}
                step={1}
                className="flex-1"
              />
              <span className="w-16 text-right text-xs tabular-nums text-muted-foreground">{radius} km</span>
            </div>
          )}
          <p className="mt-2 text-[11px] text-muted-foreground">
            Distance uses each member's most recent reported location, so members who haven't
            posted a located report yet won't appear in radius results.
          </p>
        </div>

        {filtersActive && (
          <div className="flex justify-end">
            <Button size="sm" variant="ghost" onClick={reset}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Clear filters
            </Button>
          </div>
        )}
      </Card>

      {myRow && (
        <Card className="flex items-center gap-3 border-border bg-muted/40 p-4">
          <div className="grid h-8 w-8 place-items-center rounded-full bg-muted text-sm font-semibold">
            {myRow.rank}
          </div>
          <div className="min-w-0 flex-1 text-sm">
            <span className="font-medium">Your position</span>
            <span className="text-muted-foreground"> · {myRow.civic_score ?? 0}/100 civic score</span>
          </div>
          <span className="text-xs text-muted-foreground">of {rows.length} shown</span>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <Card className="p-12 text-center text-sm text-muted-foreground">
          No members match these filters yet.
        </Card>
      ) : (
        <Card className="divide-y divide-border">
          {rows.map((u, i) => {
            const Icon = i === 0 ? Trophy : i === 1 ? Medal : i === 2 ? Award : null;
            const place = [u.city, u.country].filter(Boolean).join(", ");
            return (
              <Link
                key={u.id}
                to="/profile/$username"
                params={{ username: u.username }}
                className={cn("flex items-center gap-4 p-4 hover:bg-accent", u.id === user?.id && "bg-muted/40")}
              >
                <div className={cn(
                  "grid h-8 w-8 place-items-center rounded-full text-sm font-semibold",
                  i === 0 ? "bg-sun/20 text-sun"
                  : i === 1 ? "bg-muted text-foreground"
                  : i === 2 ? "bg-ember/20 text-ember"
                  : "bg-muted text-muted-foreground"
                )}>{u.rank}</div>
                <Avatar className="h-10 w-10">
                  <AvatarImage src={u.avatar_url ?? undefined} />
                  <AvatarFallback>{(u.full_name?.[0] || u.username[0] || "U").toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 font-medium">
                    <span className="truncate">{u.full_name || u.username}</span>
                    <AccountBadge accountType={u.account_type} verified={!!u.verified} isFounder={!!u.is_founder} />
                    {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    @{u.username} · Lvl {u.level ?? 1}
                    {place && ` · ${place}`}
                    {typeof u.distance_km === "number" && radiusOn && ` · ${u.distance_km} km away`}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-semibold tabular-nums">{u.civic_score ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">civic score</div>
                </div>
              </Link>
            );
          })}
        </Card>
      )}
    </div>
  );
}

function FilterSelect({
  label, value, onChange, placeholder, options, myValue,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: Option[];
  myValue?: string | null;
}) {
  const list = useMemo(() => {
    if (!myValue) return options;
    const rest = options.filter(o => o.value !== myValue);
    return [{ value: myValue, label: `${myValue} (mine)` }, ...rest];
  }, [options, myValue]);

  return (
    <div>
      <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 text-sm">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value={ANY}>{placeholder}</SelectItem>
          {list.map(o => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
